import { millisecondsToSeconds, secondsToMilliseconds } from 'date-fns';
import { secondsInDay } from 'date-fns/constants';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { hasValue } from 'src/shared/utils/common';
import { downloadFile } from 'src/shared/utils/download-file';
import { PlanVersionType } from 'src/store/comparison/types';
import { ExportService } from 'src/store/export-service';
import { EXPORT_TAGS } from 'src/store/export-service/consts';
import { TExportData } from 'src/store/export-service/export-service';
import { NotificationsStore } from 'src/store/notifications-store/notifications-store';
import { PlanVersionsService } from 'src/store/plan-version/plan-versions-service';

type TExportModalData = {
  startDate: number;
  endDate: number | null;
  wellList: boolean;
  gantt: boolean;
  wellPadPreparations: boolean;
};

const DEFAULT_MODAL_DATA_WITHOUT_START_DATE = {
  endDate: null,
  wellList: false,
  gantt: false,
  wellPadPreparations: false,
};

// DatePicker2V сейчас при выборе даты возвращает выбранную дату, но текущее время пользователя. Эта утилка сбрасывает время до 00:00:00
export const fixDatePickerTime = (dateIsSec: number): number => {
  return Math.floor(dateIsSec / secondsInDay) * secondsInDay;
};

export class ExportStore {
  private readonly exportService: ExportService;
  private readonly planVersionService: PlanVersionsService;
  private readonly notifications: NotificationsStore;
  @observable viewData: TExportModalData;
  @observable isModalOpen = false;

  constructor(
    exportService: ExportService,
    planVersionService: PlanVersionsService,
    notifications: NotificationsStore,
  ) {
    this.exportService = exportService;
    this.planVersionService = planVersionService;
    this.notifications = notifications;
    const startDate = fixDatePickerTime(millisecondsToSeconds(Date.now()));

    this.viewData = {
      startDate,
      ...DEFAULT_MODAL_DATA_WITHOUT_START_DATE,
    };

    makeObservable(this);
  }

  private getFixedDateWithTimezoneOffset(dateInUTC: number): number {
    const dateInst = new Date(secondsToMilliseconds(dateInUTC));
    const timezoneOffsetInSec = dateInst.getTimezoneOffset() * 60;

    return fixDatePickerTime(dateInUTC) + timezoneOffsetInSec;
  }

  private serializeViewDataToExportData(): TExportData {
    const _data = { ...this.viewData };

    _data.startDate = this.getFixedDateWithTimezoneOffset(_data.startDate);

    if (hasValue(_data.endDate)) {
      _data.endDate = this.getFixedDateWithTimezoneOffset(_data.endDate);
    }

    const { startDate, endDate } = _data;

    const exportTags = ExportStore.transformViewDataTagsToExportDataTags(_data);

    return {
      startDate,
      endDate,
      exportTags,
    };
  }

  private validate(): boolean {
    const isTagsChosen = (() => {
      const { wellList, gantt, wellPadPreparations } = this.viewData;
      return wellList || gantt || wellPadPreparations;
    })();

    if (!isTagsChosen) {
      this.notifications.showErrorMessageT('Выберитие хотя бы 1 опцию экспорта');

      return false;
    }

    return true;
  }

  @computed
  get isWPPAvailable(): boolean {
    const { currentVersion } = this.planVersionService;

    if (!currentVersion) {
      console.error('cannot find current planVersion');
      return false;
    }

    const versionType = currentVersion.data.versionType;
    return versionType === PlanVersionType.public || versionType === PlanVersionType.current;
  }

  @computed
  get isImporting(): boolean {
    return this.exportService.isImporting;
  }

  @computed
  get file(): File | null {
    return this.exportService.file;
  }

  @computed
  get error(): string | null {
    return this.exportService.error;
  }

  @action.bound
  updateData(data: Partial<TExportModalData>): void {
    const _data = { ...data };

    if (hasValue(data.endDate)) {
      _data.endDate = fixDatePickerTime(data.endDate);
    }

    this.viewData = { ...this.viewData, ..._data };
  }

  @action.bound
  setIsModalOpen(isOpen: boolean): void {
    this.isModalOpen = isOpen;
  }

  export = async (): Promise<void> => {
    if (!this.validate()) {
      return;
    }

    const serializedData = this.serializeViewDataToExportData();

    this.setIsModalOpen(false);

    await this.exportService.exportRequest(serializedData);

    if (this.exportService.file) {
      downloadFile(this.exportService.file);
      this.exportService.removeFile();
    }
  };

  cancelImport = () => {
    this.exportService.cancelImport();
    this.resetModalData();
  };

  resetModalData = (): void => {
    setTimeout(() => {
      runInAction(() => {
        const startDate = fixDatePickerTime(millisecondsToSeconds(Date.now()));
        this.viewData = { startDate, ...DEFAULT_MODAL_DATA_WITHOUT_START_DATE };
      });
    }, 300);
  };

  private static transformViewDataTagsToExportDataTags(
    data: Omit<TExportModalData, 'startDate' | 'endDate'>,
  ): EXPORT_TAGS[] {
    const tags: EXPORT_TAGS[] = [];

    if (data.wellList) {
      tags.push(EXPORT_TAGS.wellList);
    }

    if (data.gantt) {
      tags.push(EXPORT_TAGS.gantt);
    }

    if (data.wellPadPreparations) {
      tags.push(EXPORT_TAGS.wellPadPreparations);
    }

    return tags;
  }
}
